import React, { useState, useEffect } from "react";
import { Plus, Star, Users, Settings } from "lucide-react";
import UserRoleModal from "./UserRoleModal/UserRoleModal";
import customFetch from "../../../helpers/axios";
import { normalizeApiResponse } from "../../../helpers/apiUtils";
import "./UserRoles.css";
import Combobox from "../../components/Combobox/Combobox";
import UserStatCard from "../../components/UserStatCard/UserStatCard";
import "../../components/UserStatCard/UserStatCard.css";
import useUser from "../../../hooks/useUser";
import Button from "../../components/Buttons/Button/Button";
import SearchInput from "../../components/SearchInput/SearchInput";
import { useTranslation } from "react-i18next";
import useFormatDate from "../../../hooks/useFormatDate";

export default function UserRoles() {
  const { t } = useTranslation('userRoles');
  const formatDate = useFormatDate();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        setLoading(true);
        const response = await customFetch.get("/api/roles/");
        const normalized = normalizeApiResponse(response.data);
        setLevels(normalized.results);
      } catch (error) {
        console.error("Failed to fetch user levels:", error);
        setLevels([]);
      } finally {
        setLoading(false);
      }
    };

    if (user && user.access) {
      fetchLevels();
    }
  }, [user]);


  const [searchTerm, setSearchTerm] = useState("");
  const [creditFilter, setcreditFilter] = useState("all");
  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);

  // Filtrar niveles
  const filteredLevels = levels.filter((level) => {
    const matchesSearch =
      !searchTerm ||
      (level.name && level.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const limit = level.credit_limit;
    const numeric = typeof limit === "number" ? limit : null; // null => Unlimited

    const matchescredit =
      creditFilter === "all" ||
      (creditFilter === "low" && numeric !== null && numeric <= 10) ||
      (creditFilter === "medium" && numeric !== null && numeric > 10 && numeric <= 50) ||
      (creditFilter === "high" && (numeric === null || numeric > 50));

    return matchesSearch && matchescredit;
  });

  const handleEditLevel = (level) => {
    setSelectedLevel(level);
    setIsLevelModalOpen(true);
  };

  // Función para refrescar la lista de niveles
  const refreshLevels = async () => {
    try {
      setLoading(true);
      const response = await customFetch.get("/api/roles/");
      const normalized = normalizeApiResponse(response.data);
      setLevels(normalized.results);
    } catch (error) {
      console.error("Failed to refresh levels:", error);
    } finally {
      setLoading(false);
    }
  };

  // Crear o editar nivel
  const handleSaveLevel = async (levelData) => {
    try {
      if (selectedLevel) {
        // PATCH - editar existente
        const response = await customFetch.patch(`/api/roles/${selectedLevel.id}/`, levelData);
        const updatedLevel = response.data;
        
        // Verificar si la respuesta tiene ID, sino refrescar datos
        if (updatedLevel && updatedLevel.id) {
          setLevels((prev) =>
            prev.map((l) => (l.id === selectedLevel.id ? updatedLevel : l))
          );
        } else {
          console.warn('No complete data in PATCH response, refreshing...');
          await refreshLevels();
        }
      } else {
        // POST - crear nuevo
        const response = await customFetch.post("/api/roles/", levelData);
        const newLevel = response.data;
        console.log('New level created:', newLevel); // Debug log
        
        // Verificar si el ID está presente en la respuesta
        if (newLevel && newLevel.id) {
          setLevels((prev) => [...prev, newLevel]);
        } else {
          // Si no hay ID completo, refrescar los datos del servidor
          console.warn('No complete data in POST response, refreshing...');
          await refreshLevels();
        }
      }
    } catch (error) {
      console.error("Error saving level:", error);
      throw error;
    }
  };

  // Eliminar nivel
  const handleDeleteLevel = async (levelId) => {
    try {
      await customFetch.delete(`/api/roles/${levelId}/`);
      // Actualizar el estado local removiendo el nivel eliminado
      setLevels((prev) => prev.filter((l) => l.id !== levelId));
    } catch (error) {
      console.error("Error deleting level:", error);
      // Si hay error, refrescar los datos para asegurar consistencia
      await refreshLevels();
      throw error;
    }
  };


  const getCreditLimitBadgeClass = (creditLimit) => {
    if (creditLimit === null || creditLimit > 100) return "filter-purple"; // Unlimited or very high
    if (creditLimit <= 10) return "filter-blue";
    if (creditLimit <= 50) return "filter-green";
    return "filter-yellow";
  };
  const totalUsers = levels.reduce((sum, level) => sum + (level.users_count || 0), 0);

  if (loading) {
    return (
      <div className="user-roles-root">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">{t('page.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-roles-root">
      {/* Page Header */}
      <div className="user-roles-header">
        <div>
          <h1 className="user-roles-title">{t('page.title')}</h1>
          <p className="user-roles-subtitle">
            {t('page.subtitle')}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="primary"
            onClick={() => {
              setSelectedLevel(null);
              setIsLevelModalOpen(true);
            }}
            leftIcon={<Plus size={16} />}
          >
            {t('page.buttonNew')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="user-roles-stats">
        <UserStatCard
          label={t('page.stats.totalLevels')}
          value={levels.length}
          Icon={Star}
          iconColor="#039bff"
        />
        <UserStatCard
          label={t('page.stats.totalUsers')}
          value={totalUsers}
          Icon={Users}
          iconColor="#15803d"
        />
        <UserStatCard
          label={t('page.stats.activeLevels')}
          value={levels.filter(l => (l.users_count || 0) > 0).length}
          Icon={Settings}
          iconColor="#6366f1"
        />
      </div>

      {/* Filters and Search */}
      <div className="user-roles-table-section">
        <div className="user-roles-table-header">
          <div className="text-lg font-semibold">
            {t('page.sectionTitle')}
          </div>

          <div className="user-roles-searchbar">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('page.searchPlaceholder')}
              className="flex-1"
            />

            <Combobox
              value={creditFilter}
              onChange={setcreditFilter}
              options={[
                { value: "all", label: t('page.filterAllcredit') },
                { value: "low", label: t('page.filterLow') },
                { value: "medium", label: t('page.filterMedium') },
                { value: "high", label: t('page.filterHigh') },
              ]}
              buttonClass="user-roles-filter-select"
            />
          </div>
        </div>

        {/* Levels Table */}
        <div className="card">
          <div className="card-content p-0">
            <div className="overflow-x-auto">
              <table className="user-table">
                <thead className="table-horizontal-lines">
                  <tr>
                    <th>{t('page.table.level')}</th>
                    <th>{t('page.table.creditLimit')}</th>
                    <th>{t('page.table.defaultLLM')}</th>
                    <th>{t('page.table.usersCount')}</th>
                    <th>{t('page.table.created')}</th>
                    {/* Actions removed; rows are clickable */}
                  </tr>
                </thead>
                <tbody>
                  {filteredLevels.map((level) => (
                    <tr
                      key={level.id}
                      className="table-horizontal-lines"
                      style={{
                        borderColor: "var(--muted)", cursor: 'pointer'
                      }}
                      onClick={() => handleEditLevel(level)}
                    >
                      <td>
                        <div className="flex items-center space-x-3">
                          <div className="owly-sidebar-avatar bg-gradient-to-r from-blue-500 to-purple-600">
                            <span className="text-white font-medium">
                              {level.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="owly-user-management-name">
                              {level.name}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="user-roles-table-credit">
                        <span
                          className={`badge ${getCreditLimitBadgeClass(level.credit_limit ?? null)}`}
                          style={{ fontWeight: 600 }}
                        >
                          {level.credit_limit === null ? t('modal.badges.unlimited') : `${(level.credit_limit || 0).toLocaleString()} ${t('modal.badges.perDay')}`}
                        </span>
                      </td>

                      <td className="user-roles-default-llm">
                        {(() => {
                          const v = level.default_llm;
                          if (!v && v !== 0) return t('page.table.none');
                          if (typeof v === 'number') return v;
                          if (typeof v === 'object' && v) return v.name || t('page.table.none');
                          // string (could be id or name)
                          return v;
                        })()}
                      </td>

                      <td>
                        <span className="th-waffle-flags">
                          {level.users_count || 0} {t('modal.badges.users')}
                        </span>
                      </td>

                      <td>
                        <span className="levels-date">
                          {formatDate(level.created)}
                        </span>
                      </td>

                      {/* Actions cell removed */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {filteredLevels.length === 0 && !loading && (
        <div className="text-center py-8">
          <Star size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('page.emptyTitle')}
          </h3>
          <p className="text-gray-600">
            {t('page.emptyDesc')}
          </p>
        </div>
      )}

      {/* User Role Modal */}
      <UserRoleModal
        isOpen={isLevelModalOpen}
        onClose={() => {
          setIsLevelModalOpen(false);
          setSelectedLevel(null);
        }}
        role={selectedLevel}
        onSave={handleSaveLevel}
        onDelete={handleDeleteLevel}
      />

    </div>
  );
}
